import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import * as z from "zod";
import React, { useState, useMemo, useCallback } from "react";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useReferenceUserRoles } from "@/hooks/users/useReferenceUserRoles";
import { useTimezones } from "@/hooks/data/useTimezones";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, Plus, Trash2, ChevronDown, ChevronUp, Copy } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { format, eachDayOfInterval } from "date-fns";
import { cn } from "@/lib/utils";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

// Create schema for add mode (password required)
const addUserFormSchema = z.object({
  firstname: z.string().trim().min(1, "First name is required"),
  lastname: z.string().trim().min(1, "Last name is required"),
  email: z.string().trim().email("Please enter a valid email address"),
  phonenumber: z.string()
    .trim()
    .min(10, "Phone number must be at least 10 digits")
    .regex(/^\d{10,}$/, "Phone number must contain only digits"),
  password: z.string().min(6, "Password must be at least 6 characters"),
  usertype: z.string().min(1, "Please select a user type"),
});

// Create schema for edit mode (password optional)
const editUserFormSchema = z.object({
  firstname: z.string().trim().min(1, "First name is required"),
  lastname: z.string().trim().min(1, "Last name is required"),
  email: z.string().trim().email("Please enter a valid email address"),
  phonenumber: z.string()
    .trim()
    .min(10, "Phone number must be at least 10 digits")
    .regex(/^\d{10,}$/, "Phone number must contain only digits"),
  password: z.string().optional().or(z.string().min(6, "Password must be at least 6 characters")),
  usertype: z.string().min(1, "Please select a user type"),
});

type UserFormData = z.infer<typeof addUserFormSchema>;

interface TimeSlot {
  id: string;
  fromTime: string;
  toTime: string;
  error?: string;
  availabilityDayTimeId?: number | null;
}

interface DaySlots {
  enabled: boolean;
  slots: TimeSlot[];
}

interface InitialScheduleConfig {
  resourceAvailabilityId: number | null;
  fromDate: string;
  toDate: string;
  timezone: string;
  daySlots: Record<string, {
    enabled: boolean;
    slots: Array<{
      id: string;
      fromTime: string;
      toTime: string;
      availabilityDayTimeId?: number | null;
    }>;
  }>;
}

interface UserFormProps {
  initialData?: Partial<UserFormData>;
  initialScheduleConfig?: InitialScheduleConfig | null;
  title: string;
  onSubmit: (data: UserFormData & { 
    scheduleConfig?: {
      fromDate: string;
      toDate: string;
      timezone: string;
      daySlots: Record<string, DaySlots>;
    }
  }) => Promise<void>;
  isLoading?: boolean;
  returnPath?: string;
  isEditMode?: boolean;
}

export const UserForm = ({ initialData, initialScheduleConfig, title, onSubmit, isLoading = false, returnPath, isEditMode = false }: UserFormProps) => {
  const navigate = useNavigate();
  const { userRoles, loading: rolesLoading } = useReferenceUserRoles();
  const { timezones, loading: timezonesLoading } = useTimezones();
  
  // Schedule state - initialize from initialScheduleConfig if available
  const [fromDate, setFromDate] = useState<Date | undefined>(() => {
    if (initialScheduleConfig?.fromDate) {
      return new Date(initialScheduleConfig.fromDate);
    }
    return undefined;
  });
  const [toDate, setToDate] = useState<Date | undefined>(() => {
    if (initialScheduleConfig?.toDate) {
      return new Date(initialScheduleConfig.toDate);
    }
    return undefined;
  });
  const [selectedTimezone, setSelectedTimezone] = useState(initialScheduleConfig?.timezone || "US/Pacific");
  const [dateError, setDateError] = useState<string>("");
  const [daysInRange, setDaysInRange] = useState<string[]>([]);
  const [collapsedDays, setCollapsedDays] = useState<Record<string, boolean>>({});
  
  const [appointmentSlots, setAppointmentSlots] = useState<Record<string, DaySlots>>(() => {
    if (initialScheduleConfig?.daySlots) {
      return initialScheduleConfig.daySlots;
    }
    return {
      Monday: { enabled: false, slots: [{ id: "Monday-1", fromTime: "", toTime: "" }] },
      Tuesday: { enabled: false, slots: [{ id: "Tuesday-1", fromTime: "", toTime: "" }] },
      Wednesday: { enabled: false, slots: [{ id: "Wednesday-1", fromTime: "", toTime: "" }] },
      Thursday: { enabled: false, slots: [{ id: "Thursday-1", fromTime: "", toTime: "" }] },
      Friday: { enabled: false, slots: [{ id: "Friday-1", fromTime: "", toTime: "" }] },
      Saturday: { enabled: false, slots: [{ id: "Saturday-1", fromTime: "", toTime: "" }] },
      Sunday: { enabled: false, slots: [{ id: "Sunday-1", fromTime: "", toTime: "" }] }
    };
  });
  
  const form = useForm<UserFormData>({
    resolver: zodResolver(isEditMode ? editUserFormSchema : addUserFormSchema),
    mode: "onChange",
    defaultValues: {
      firstname: initialData?.firstname || "",
      lastname: initialData?.lastname || "",
      email: initialData?.email || "",
      phonenumber: initialData?.phonenumber || "",
      password: initialData?.password || "",
      usertype: initialData?.usertype || undefined,
    },
  });

  const watchedUserType = form.watch("usertype");
  const watchedFormValues = form.watch();

  // Create stable reference for initial schedule config comparison
  const initialScheduleConfigRef = useMemo(() => {
    if (!initialScheduleConfig) return null;
    return {
      fromDate: initialScheduleConfig.fromDate,
      toDate: initialScheduleConfig.toDate,
      timezone: initialScheduleConfig.timezone,
      daySlots: initialScheduleConfig.daySlots
    };
  }, []);

  // Check if form has changes (for edit mode)
  const hasFormChanges = useCallback(() => {
    if (!isEditMode) return true; // Always allow save in add mode
    
    // Compare form fields
    const currentFirstname = watchedFormValues.firstname?.trim() || '';
    const currentLastname = watchedFormValues.lastname?.trim() || '';
    const currentEmail = watchedFormValues.email?.trim() || '';
    const currentPhone = watchedFormValues.phonenumber?.trim() || '';
    const currentPassword = watchedFormValues.password?.trim() || '';
    const currentUsertype = watchedFormValues.usertype || '';

    const initialFirstname = initialData?.firstname?.trim() || '';
    const initialLastname = initialData?.lastname?.trim() || '';
    const initialEmail = initialData?.email?.trim() || '';
    const initialPhone = initialData?.phonenumber?.trim() || '';
    const initialUsertype = initialData?.usertype || '';

    // Check basic form field changes
    if (currentFirstname !== initialFirstname) return true;
    if (currentLastname !== initialLastname) return true;
    if (currentEmail !== initialEmail) return true;
    if (currentPhone !== initialPhone) return true;
    if (currentUsertype !== initialUsertype) return true;
    if (currentPassword && currentPassword.length > 0) return true; // Password changed

    // Compare schedule config
    const currentFromDate = fromDate ? format(fromDate, 'yyyy-MM-dd') : '';
    const currentToDate = toDate ? format(toDate, 'yyyy-MM-dd') : '';
    const initialFromDate = initialScheduleConfigRef?.fromDate || '';
    const initialToDate = initialScheduleConfigRef?.toDate || '';

    if (currentFromDate !== initialFromDate) return true;
    if (currentToDate !== initialToDate) return true;
    if (selectedTimezone !== (initialScheduleConfigRef?.timezone || 'US/Pacific')) return true;

    // Compare day slots
    const initialDaySlots = initialScheduleConfigRef?.daySlots || {};
    for (const day of Object.keys(appointmentSlots)) {
      const currentDay = appointmentSlots[day];
      const initialDay = initialDaySlots[day];

      if (!initialDay) {
        if (currentDay.enabled) return true;
        continue;
      }

      if (currentDay.enabled !== initialDay.enabled) return true;

      if (currentDay.enabled && initialDay.enabled) {
        if (currentDay.slots.length !== initialDay.slots.length) return true;
        
        for (let i = 0; i < currentDay.slots.length; i++) {
          const currentSlot = currentDay.slots[i];
          const initialSlot = initialDay.slots[i];
          if (!initialSlot) return true;
          if (currentSlot.fromTime !== initialSlot.fromTime) return true;
          if (currentSlot.toTime !== initialSlot.toTime) return true;
        }
      }
    }

    return false;
  }, [watchedFormValues, initialData, isEditMode, fromDate, toDate, selectedTimezone, appointmentSlots, initialScheduleConfigRef]);

  const formHasChanges = hasFormChanges();

  // Time options
  const hourOptions = Array.from({ length: 12 }, (_, i) => ({
    value: String(i + 1),
    label: String(i + 1)
  }));

  const minuteOptions = [
    { value: '00', label: '00' },
    { value: '15', label: '15' },
    { value: '30', label: '30' },
    { value: '45', label: '45' }
  ];

  const periodOptions = [
    { value: 'AM', label: 'AM' },
    { value: 'PM', label: 'PM' }
  ];

  // Helper functions
  const parseTimeString = (timeStr: string) => {
    if (!timeStr) return { hour: '', minute: '', period: '' };
    const match = timeStr.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!match) return { hour: '', minute: '', period: '' };
    return {
      hour: match[1],
      minute: match[2],
      period: match[3].toUpperCase()
    };
  };

  const constructTimeString = (hour: string, minute: string, period: string) => {
    if (!hour || !minute || !period) return '';
    return `${hour}:${minute} ${period}`;
  };

  // Calculate days in range
  const calculateDaysInRange = (start: Date | undefined, end: Date | undefined) => {
    if (!start || !end) {
      setDaysInRange([]);
      return;
    }
    
    const days = eachDayOfInterval({ start, end });
    const dayNames = days.map(date => {
      const dayIndex = date.getDay();
      return ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][dayIndex];
    });
    
    const uniqueDays = Array.from(new Set(dayNames));
    setDaysInRange(uniqueDays);
  };

  React.useEffect(() => {
    calculateDaysInRange(fromDate, toDate);
  }, [fromDate, toDate]);

  // Slot management functions
  const toggleDay = (day: string) => {
    const currentDayData = appointmentSlots[day];
    const newEnabled = !currentDayData.enabled;
    
    setAppointmentSlots(prev => ({
      ...prev,
      [day]: { ...prev[day], enabled: newEnabled }
    }));
    
    if (newEnabled) {
      setCollapsedDays(prev => ({ ...prev, [day]: false }));
    }
  };

  const toggleCollapse = (day: string) => {
    if (!appointmentSlots[day].enabled) return;
    setCollapsedDays(prev => ({ ...prev, [day]: !prev[day] }));
  };

  const addSlot = (day: string) => {
    setAppointmentSlots(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        slots: [...prev[day].slots, { id: `${day}-${Date.now()}`, fromTime: "", toTime: "" }]
      }
    }));
  };

  const removeSlot = (day: string, slotId: string) => {
    setAppointmentSlots(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        slots: prev[day].slots.filter(s => s.id !== slotId)
      }
    }));
  };

  const updateSlot = (day: string, slotId: string, field: 'fromTime' | 'toTime', value: string) => {
    setAppointmentSlots(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        slots: prev[day].slots.map(s => 
          s.id === slotId ? { ...s, [field]: value } : s
        )
      }
    }));
  };

  // Copy slots from a day to all other days
  const copySlotsToAllDays = (sourceDay: string) => {
    const sourceSlots = appointmentSlots[sourceDay].slots;
    
    // Only copy if source day has valid slots
    if (!sourceSlots.length || !sourceSlots.some(s => s.fromTime && s.toTime)) {
      return;
    }
    
    setAppointmentSlots(prev => {
      const updated = { ...prev };
      
      orderedDays.filter(day => daysInRange.includes(day) && day !== sourceDay).forEach(targetDay => {
        // Create new slots with unique IDs for the target day
        const copiedSlots = sourceSlots.map((slot, index) => ({
          ...slot,
          id: `${targetDay}-${Date.now()}-${index}`,
          availabilityDayTimeId: null // Reset ID for new slots
        }));
        
        updated[targetDay] = {
          enabled: true,
          slots: copiedSlots
        };
      });
      
      return updated;
    });
    
    // Expand all days that received slots
    setCollapsedDays(prev => {
      const updated = { ...prev };
      orderedDays.filter(day => daysInRange.includes(day)).forEach(day => {
        updated[day] = false;
      });
      return updated;
    });
  };

  const handleSubmit = async (data: UserFormData) => {
    const scheduleConfig = watchedUserType ? {
      fromDate: fromDate ? format(fromDate, 'yyyy-MM-dd') : '',
      toDate: toDate ? format(toDate, 'yyyy-MM-dd') : '',
      timezone: selectedTimezone,
      daySlots: appointmentSlots
    } : undefined;
    
    await onSubmit({ ...data, scheduleConfig });
  };

  const handleCancel = () => {
    navigate(returnPath || "/admin/users");
  };

  const orderedDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  return (
    <div className="container max-w-2xl mx-auto p-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl font-semibold text-foreground">{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="firstname"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>First Name *</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="Enter first name" 
                          {...field} 
                          className="bg-background border-input"
                          disabled={isLoading}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="lastname"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Last Name *</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="Enter last name" 
                          {...field}
                          className="bg-background border-input"
                          disabled={isLoading}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email Address *</FormLabel>
                    <FormControl>
                      <Input 
                        type="email"
                        placeholder="Enter email address" 
                        {...field}
                        className="bg-background border-input"
                        autoComplete="off"
                        disabled={isLoading}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="phonenumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone Number *</FormLabel>
                    <FormControl>
                      <Input 
                        type="tel"
                        placeholder="Enter 10 digit phone number" 
                        {...field}
                        className="bg-background border-input"
                        maxLength={10}
                        disabled={isLoading}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Password {!isEditMode && "*"}</FormLabel>
                    <FormControl>
                      <Input 
                        type="password"
                        placeholder={isEditMode ? "Leave blank to keep current password" : "Minimum 6 characters"} 
                        {...field}
                        className="bg-background border-input"
                        autoComplete="new-password"
                        disabled={isLoading}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="usertype"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>User Type *</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value} disabled={isLoading || rolesLoading}>
                      <FormControl>
                        <SelectTrigger className="bg-background border-input">
                          <SelectValue placeholder={rolesLoading ? "Loading roles..." : "Select user type"} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {userRoles.map((role) => (
                          <SelectItem key={role.role_id} value={role.role}>
                            {role.role}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Schedule Configuration - Always visible */}
              <div className="space-y-4 border-t pt-6">
                  <h3 className="text-lg font-semibold text-foreground">User Schedule Configuration</h3>
                  
                  {/* Date Range */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>From Date *</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal",
                              !fromDate && "text-muted-foreground"
                            )}
                            disabled={isLoading}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {fromDate ? format(fromDate, "MM/dd/yyyy") : "Select from date"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0 bg-popover z-50" align="start">
                          <Calendar
                            mode="single"
                            selected={fromDate}
                            onSelect={(date) => {
                              setFromDate(date);
                              if (date && toDate && date > toDate) {
                                setDateError("From date cannot be after end date");
                              } else {
                                setDateError("");
                              }
                            }}
                            initialFocus
                            className="pointer-events-auto"
                          />
                        </PopoverContent>
                      </Popover>
                    </div>

                    <div className="space-y-2">
                      <Label>End Date *</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal",
                              !toDate && "text-muted-foreground"
                            )}
                            disabled={isLoading}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {toDate ? format(toDate, "MM/dd/yyyy") : "Select end date"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0 bg-popover z-50" align="start">
                          <Calendar
                            mode="single"
                            selected={toDate}
                            onSelect={(date) => {
                              setToDate(date);
                              if (fromDate && date && fromDate > date) {
                                setDateError("End date cannot be before from date");
                              } else {
                                setDateError("");
                              }
                            }}
                            disabled={(date) => fromDate ? date < fromDate : false}
                            initialFocus
                            className="pointer-events-auto"
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                  {dateError && <p className="text-sm text-destructive">{dateError}</p>}

                  {/* Timezone */}
                  <div className="space-y-2">
                    <Label>Timezone *</Label>
                    <Select 
                      value={selectedTimezone} 
                      onValueChange={setSelectedTimezone}
                      disabled={isLoading || timezonesLoading}
                    >
                      <SelectTrigger className="bg-background border-input">
                        <SelectValue placeholder={timezonesLoading ? "Loading..." : "Select timezone"} />
                      </SelectTrigger>
                      <SelectContent>
                        {timezones.map((tz) => (
                          <SelectItem key={tz.timezone} value={tz.timezone}>
                            {tz.timezone}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Day Slots */}
                  {fromDate && toDate && (
                    <div className="space-y-3">
                      <Label>User Schedule</Label>
                      <div className="space-y-2">
                        {orderedDays.filter(day => daysInRange.includes(day)).map((day) => (
                          <div key={day} className="border rounded-lg overflow-hidden">
                            <div className="flex items-center justify-between p-3 bg-muted/30">
                              <div className="flex items-center gap-3">
                                <Checkbox
                                  checked={appointmentSlots[day].enabled}
                                  onCheckedChange={() => toggleDay(day)}
                                  disabled={isLoading}
                                />
                                <span className="font-medium text-sm">{day}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                {/* Copy to All Days button - show on first enabled day with slots */}
                                {appointmentSlots[day].enabled && 
                                  orderedDays.filter(d => daysInRange.includes(d) && appointmentSlots[d].enabled).indexOf(day) === 0 && (
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          type="button"
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => copySlotsToAllDays(day)}
                                          className="h-8 px-2 text-muted-foreground hover:text-foreground"
                                          disabled={isLoading || !appointmentSlots[day].slots.some(s => s.fromTime && s.toTime)}
                                        >
                                          <Copy className="h-4 w-4 mr-1" />
                                          <span className="text-xs">Copy to All Days</span>
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent side="top">
                                        <p>Copy these time slots to all other days</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                )}
                                {appointmentSlots[day].enabled && (
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => toggleCollapse(day)}
                                  >
                                    {collapsedDays[day] ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
                                  </Button>
                                )}
                              </div>
                            </div>

                            <Collapsible open={appointmentSlots[day].enabled && !collapsedDays[day]}>
                              <CollapsibleContent>
                                <div className="p-3 space-y-3 bg-background">
                                  {appointmentSlots[day].slots.map((slot, slotIndex) => (
                                    <div key={slot.id} className="flex items-center gap-2 flex-wrap">
                                      {/* From Time */}
                                      <div className="flex items-center gap-1">
                                        <Select
                                          value={parseTimeString(slot.fromTime).hour}
                                          onValueChange={(val) => {
                                            const current = parseTimeString(slot.fromTime);
                                            updateSlot(day, slot.id, 'fromTime', constructTimeString(val, current.minute || '00', current.period || 'AM'));
                                          }}
                                        >
                                          <SelectTrigger className="w-16 h-8 text-xs">
                                            <SelectValue placeholder="HH" />
                                          </SelectTrigger>
                                          <SelectContent>
                                            {hourOptions.map(h => (
                                              <SelectItem key={h.value} value={h.value}>{h.label}</SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                        <span>:</span>
                                        <Select
                                          value={parseTimeString(slot.fromTime).minute}
                                          onValueChange={(val) => {
                                            const current = parseTimeString(slot.fromTime);
                                            updateSlot(day, slot.id, 'fromTime', constructTimeString(current.hour || '12', val, current.period || 'AM'));
                                          }}
                                        >
                                          <SelectTrigger className="w-16 h-8 text-xs">
                                            <SelectValue placeholder="MM" />
                                          </SelectTrigger>
                                          <SelectContent>
                                            {minuteOptions.map(m => (
                                              <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                        <Select
                                          value={parseTimeString(slot.fromTime).period}
                                          onValueChange={(val) => {
                                            const current = parseTimeString(slot.fromTime);
                                            updateSlot(day, slot.id, 'fromTime', constructTimeString(current.hour || '12', current.minute || '00', val));
                                          }}
                                        >
                                          <SelectTrigger className="w-16 h-8 text-xs">
                                            <SelectValue placeholder="AM" />
                                          </SelectTrigger>
                                          <SelectContent>
                                            {periodOptions.map(p => (
                                              <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                      </div>

                                      <span className="text-muted-foreground text-sm">to</span>

                                      {/* To Time */}
                                      <div className="flex items-center gap-1">
                                        <Select
                                          value={parseTimeString(slot.toTime).hour}
                                          onValueChange={(val) => {
                                            const current = parseTimeString(slot.toTime);
                                            updateSlot(day, slot.id, 'toTime', constructTimeString(val, current.minute || '00', current.period || 'AM'));
                                          }}
                                        >
                                          <SelectTrigger className="w-16 h-8 text-xs">
                                            <SelectValue placeholder="HH" />
                                          </SelectTrigger>
                                          <SelectContent>
                                            {hourOptions.map(h => (
                                              <SelectItem key={h.value} value={h.value}>{h.label}</SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                        <span>:</span>
                                        <Select
                                          value={parseTimeString(slot.toTime).minute}
                                          onValueChange={(val) => {
                                            const current = parseTimeString(slot.toTime);
                                            updateSlot(day, slot.id, 'toTime', constructTimeString(current.hour || '12', val, current.period || 'AM'));
                                          }}
                                        >
                                          <SelectTrigger className="w-16 h-8 text-xs">
                                            <SelectValue placeholder="MM" />
                                          </SelectTrigger>
                                          <SelectContent>
                                            {minuteOptions.map(m => (
                                              <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                        <Select
                                          value={parseTimeString(slot.toTime).period}
                                          onValueChange={(val) => {
                                            const current = parseTimeString(slot.toTime);
                                            updateSlot(day, slot.id, 'toTime', constructTimeString(current.hour || '12', current.minute || '00', val));
                                          }}
                                        >
                                          <SelectTrigger className="w-16 h-8 text-xs">
                                            <SelectValue placeholder="PM" />
                                          </SelectTrigger>
                                          <SelectContent>
                                            {periodOptions.map(p => (
                                              <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                      </div>

                                      {/* Remove slot */}
                                      {appointmentSlots[day].slots.length > 1 && (
                                        <Button
                                          type="button"
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => removeSlot(day, slot.id)}
                                          className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </Button>
                                      )}
                                    </div>
                                  ))}

                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => addSlot(day)}
                                    className="w-full"
                                  >
                                    <Plus className="h-4 w-4 mr-1" /> Add Time Slot
                                  </Button>
                                </div>
                              </CollapsibleContent>
                            </Collapsible>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

              <div className="flex gap-4 justify-end">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={handleCancel}
                  disabled={isLoading}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="bg-primary text-primary-foreground hover:bg-primary-hover"
                  disabled={isLoading || !form.formState.isValid || (isEditMode && !formHasChanges)}
                >
                  {isLoading ? "Saving..." : "Save"}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
};